/**
 * Web Notification scheduling.
 *
 * Bridges Android's scheduleNotification / scheduleReminder / cancelReminder
 * to the browser Notification API. State is persisted in localStorage so
 * pending reminders survive a reload — re-armed on bridge init.
 *
 * Limitations vs Android (AlarmManager):
 *   - Timers run on setTimeout/setInterval — only fire while the page is open
 *     OR while the service worker is alive. Closed tabs miss notifications.
 *   - For true background delivery we'd need Web Push (requires a push server).
 *
 * Where the SW is available we register notifications via the SW so they
 * remain visible after the tab is closed.
 */

const STORE_KEY = 'shell.notifications.scheduled';

interface ScheduledItem {
  /** Unique id, generated at schedule time. */
  id: string;
  /** Owning module so cancelReminder() can scope its cleanup. */
  moduleId: string;
  /** Kind of schedule. */
  kind: 'one_shot' | 'repeating';
  /** Epoch ms when the notification should fire (for one_shot). */
  fireAtMs?: number;
  /** Interval in ms (for repeating). */
  intervalMs?: number;
  title: string;
  message: string;
  /** Optional client-defined type tag (mirrors Android's `type` arg). */
  type?: string;
}

// Active in-memory timer handles, keyed by item id.
const timers = new Map<string, number>();

function readAll(): ScheduledItem[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writeAll(list: ScheduledItem[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(list));
  } catch (e) {
    console.warn('[notifications] persist failed', e);
  }
}

function showNotification(item: ScheduledItem): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  const opts: NotificationOptions = {
    body: item.message,
    icon: '/icon.svg',
    badge: '/icon.svg',
    tag: `${item.moduleId}::${item.id}`,
  };
  // Prefer the SW registration so the notification persists if the tab closes.
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) reg.showNotification(item.title, opts);
      else new Notification(item.title, opts);
    }).catch(() => { new Notification(item.title, opts); });
  } else {
    new Notification(item.title, opts);
  }
}

function arm(item: ScheduledItem): void {
  if (item.kind === 'one_shot' && item.fireAtMs != null) {
    const delay = Math.max(0, item.fireAtMs - Date.now());
    const handle = window.setTimeout(() => {
      showNotification(item);
      // Drop from store after firing.
      const list = readAll().filter((x) => x.id !== item.id);
      writeAll(list);
      timers.delete(item.id);
    }, delay);
    timers.set(item.id, handle);
  } else if (item.kind === 'repeating' && item.intervalMs != null) {
    const handle = window.setInterval(() => showNotification(item), item.intervalMs);
    timers.set(item.id, handle);
  }
}

function cancelTimer(id: string, kind: 'one_shot' | 'repeating'): void {
  const handle = timers.get(id);
  if (handle == null) return;
  if (kind === 'repeating') window.clearInterval(handle);
  else window.clearTimeout(handle);
  timers.delete(id);
}

function newId(): string {
  return `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Re-arm any pending notifications on app start. Call once from the launcher. */
export function rehydrateNotifications(): void {
  const now = Date.now();
  const list = readAll();
  const survivors: ScheduledItem[] = [];
  for (const item of list) {
    if (item.kind === 'one_shot') {
      if (item.fireAtMs != null && item.fireAtMs > now) {
        survivors.push(item);
        arm(item);
      }
      // Already-due one-shots are dropped (we missed them while closed).
    } else if (item.kind === 'repeating') {
      survivors.push(item);
      arm(item);
    }
  }
  if (survivors.length !== list.length) writeAll(survivors);
}

/**
 * Module API — match Android signatures.
 */

export function scheduleNotification(
  moduleId: string,
  seconds: number,
  title: string,
  message: string,
  type = 'test',
): void {
  ensurePermission();
  const item: ScheduledItem = {
    id: newId(),
    moduleId,
    kind: 'one_shot',
    fireAtMs: Date.now() + Math.max(0, seconds) * 1000,
    title, message, type,
  };
  const list = readAll();
  list.push(item);
  writeAll(list);
  arm(item);
}

export function scheduleReminder(
  moduleId: string,
  intervalMinutes: number,
  title: string,
  message: string,
): void {
  ensurePermission();
  // Drop any existing repeating reminder for this module (Android cancels +
  // re-arms in scheduleReminder too — matches its self-rearming behavior).
  cancelReminder(moduleId);
  const item: ScheduledItem = {
    id: newId(),
    moduleId,
    kind: 'repeating',
    intervalMs: Math.max(1, intervalMinutes) * 60_000,
    title, message,
  };
  const list = readAll();
  list.push(item);
  writeAll(list);
  arm(item);
}

export function cancelReminder(moduleId: string): void {
  const list = readAll();
  const remaining: ScheduledItem[] = [];
  for (const item of list) {
    if (item.moduleId === moduleId) {
      cancelTimer(item.id, item.kind);
    } else {
      remaining.push(item);
    }
  }
  writeAll(remaining);
}

function ensurePermission(): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'default') {
    // Fire-and-forget: user can grant later; we'll still try to show then.
    void Notification.requestPermission();
  }
}
